import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Edit, Trash2, Globe, Lock, Loader2, AlertCircle, Layers, Copy, Download,
  Check, ChevronDown, LayoutTemplate, Star, Pencil, X, Palette as PaletteIcon,
  Sparkles, Radio,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import {
  overlaysApi, facilitiesApi, colorSchemesApi, queryKeys,
  OverlayComposition, ColorScheme, RoleValue,
} from '@/lib/api';
import { useAuth } from '@/store/auth';
import { SchemeEditorModal, type ModalMode } from '@/components/SchemeEditorModal';
import { useAdminFacility } from '@/hooks/useAdminFacility';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button onClick={() => { navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }); }}
      title="Copy URL" className="ml-1 shrink-0 rounded p-0.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
      {copied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
    </button>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const SHEET_OPTIONS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];

function suggestSheetId(facilityId: string, compositions: OverlayComposition[], excludeId?: number): string {
  const used = new Set(
    compositions.filter(c => c.facilityId === facilityId && c.id !== excludeId).map(c => c.sheetId)
  );
  return SHEET_OPTIONS.find(s => !used.has(s)) ?? 'A';
}

// ─── New Overlay Modal ────────────────────────────────────────────────────────

interface NewOverlayModalProps {
  open: boolean;
  mode: 'blank' | 'template' | 'duplicate';
  template?: OverlayComposition | null;
  duplicateSource?: OverlayComposition | null;
  allCompositions: OverlayComposition[];
  onClose: () => void;
  onCreated?: (facilityId: string) => void;
  defaultFacilityId: string;
  facilities: Array<{ facilityId: string; name: string }>;
  isAdmin: boolean;
}

function NewOverlayModal({ open, mode, template, duplicateSource, allCompositions, onClose, onCreated, defaultFacilityId, facilities, isAdmin }: NewOverlayModalProps) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ facilityId: defaultFacilityId, sheetId: 'A', name: '', description: '' });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const source = mode === 'duplicate' ? duplicateSource : (mode === 'template' ? template : null);
    const baseFacilityId = source?.facilityId ?? defaultFacilityId;
    const suggestedSheet = (mode === 'blank')
      ? suggestSheetId(baseFacilityId, allCompositions)
      : suggestSheetId(baseFacilityId, allCompositions, source?.id);
    setForm({
      facilityId: baseFacilityId,
      sheetId: suggestedSheet,
      name: source ? `${source.name} copy` : '',
      description: '',
    });
    setError(null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  type CreatePayload = { facilityId: string; sheetId: string; name: string; description?: string };

  const createMutation = useMutation({
    mutationFn: (payload: CreatePayload) => {
      if (mode === 'duplicate' && duplicateSource) return overlaysApi.duplicateComposition(duplicateSource.id, payload);
      if (mode === 'template' && template) return overlaysApi.createFromTemplate(template.id, payload);
      return overlaysApi.createComposition(payload);
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: queryKeys.overlays.all }); onCreated?.(form.facilityId); onClose(); },
    onError: (err: any) => setError(err?.message || 'Failed to create'),
  });

  const handleCreate = () => {
    if (!form.name.trim()) { setError('Name is required'); return; }
    const trimmedDesc = form.description.trim();
    const base: CreatePayload = { facilityId: form.facilityId, sheetId: form.sheetId, name: form.name.trim() };
    if (trimmedDesc) base.description = trimmedDesc;
    createMutation.mutate(base);
  };

  const outputUrl = form.facilityId && form.sheetId
    ? `${window.location.origin}/overlay/${form.facilityId}/${form.sheetId}`
    : null;
  const urlConflict = allCompositions.some(
    c => c.facilityId === form.facilityId && c.sheetId === form.sheetId && c.id !== duplicateSource?.id
  );

  if (!open) return null;

  const title = mode === 'duplicate'
    ? `Duplicate "${duplicateSource?.name}"`
    : mode === 'template'
      ? `New Overlay from "${template?.name}"`
      : 'New Overlay';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl dark:bg-gray-900">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
          <button onClick={onClose} className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-4 px-6 py-4">
          {error && <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">{error}</div>}
          {mode === 'duplicate' && duplicateSource && (
            <div className="rounded-md border border-purple-200 bg-purple-50 px-3 py-2 text-sm text-purple-700 dark:border-purple-800 dark:bg-purple-900/20 dark:text-purple-300">
              All scenes and widgets will be copied. Choose a new sheet assignment below.
            </div>
          )}
          {mode === 'template' && template && (
            <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
              Scenes and widgets will be copied from the template.
            </div>
          )}
          {isAdmin && (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Facility</label>
              <select value={form.facilityId}
                onChange={e => {
                  const fid = e.target.value;
                  const suggested = suggestSheetId(fid, allCompositions, duplicateSource?.id);
                  setForm(f => ({ ...f, facilityId: fid, sheetId: suggested }));
                }}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100">
                {facilities.map(f => <option key={f.facilityId} value={f.facilityId}>{f.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Sheet</label>
            <select value={form.sheetId} onChange={e => setForm(f => ({ ...f, sheetId: e.target.value }))}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100">
              {SHEET_OPTIONS.map(s => <option key={s} value={s}>Sheet {s}</option>)}
            </select>
            {outputUrl && (
              <div className={`mt-1.5 rounded px-2.5 py-1.5 ${urlConflict ? 'bg-amber-50 dark:bg-amber-900/20' : 'bg-gray-50 dark:bg-gray-800/60'}`}>
                <p className="truncate font-mono text-[11px] text-gray-600 dark:text-gray-300">{outputUrl}</p>
                {urlConflict && (
                  <p className="mt-0.5 text-[11px] text-amber-600 dark:text-amber-400">⚠ This URL is already used by another overlay.</p>
                )}
              </div>
            )}
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Name <span className="text-red-500">*</span></label>
            <input autoFocus type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && handleCreate()}
              placeholder="e.g. Sheet A Broadcast"
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
            <input type="text" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Optional"
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100" />
          </div>
        </div>
        <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
          <Button variant="outline" onClick={onClose} disabled={createMutation.isPending}>Cancel</Button>
          <Button onClick={handleCreate} disabled={createMutation.isPending}>
            {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Scheme Picker (per-card) ─────────────────────────────────────────────────

/**
 * Render a small swatch summarizing a role value — solid color or a tiny
 * gradient strip. Pure presentation, no interactivity.
 */
function RoleSwatch({ value, title }: { value: RoleValue; title?: string }) {
  const style: React.CSSProperties =
    value.type === 'solid'
      ? { background: value.color }
      : {
          background:
            value.mode === 'linear'
              ? `linear-gradient(${value.angle}deg, ${value.stops.map(s => `${s.color} ${s.position}%`).join(', ')})`
              : `radial-gradient(circle, ${value.stops.map(s => `${s.color} ${s.position}%`).join(', ')})`,
        };
  return (
    <span
      title={title}
      className="inline-block h-3 w-3 rounded-sm border border-black/20 dark:border-white/20"
      style={style}
    />
  );
}

/**
 * Build a 3-swatch preview from a scheme: bottomBarFill, centerPanelBorder, accent.
 * Falls back to the catalogue defaults if any role is missing.
 */
function SchemePreview({ scheme }: { scheme: ColorScheme }) {
  const previewRoles = ['bottomBarFill', 'centerPanelBorder', 'accent'];
  return (
    <span className="inline-flex shrink-0 gap-0.5">
      {previewRoles.map(r => {
        const v = scheme.palette[r];
        if (!v) return null;
        return <RoleSwatch key={r} value={v} title={r} />;
      })}
    </span>
  );
}

function SchemePicker({ composition }: { composition: OverlayComposition }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [modalState, setModalState] = useState<{ open: boolean; mode: ModalMode; source: ColorScheme | null }>({
    open: false, mode: 'create', source: null,
  });
  const ref = useRef<HTMLDivElement>(null);

  // Schemes scoped to this composition's facility (plus globals — backend includes them).
  const { data: schemesData, isLoading } = useQuery({
    queryKey: queryKeys.colorSchemes.list(composition.facilityId),
    queryFn: () => colorSchemesApi.list({ facilityId: composition.facilityId }),
  });
  const schemes = schemesData?.schemes ?? [];

  const applyMutation = useMutation({
    mutationFn: (schemeId: number | null) => overlaysApi.setActiveScheme(composition.id, schemeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.overlays.all });
    },
  });

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const active = composition.activeColorScheme ?? null;
  const activeIsEditable = active && !active.isGlobal && active.facilityId === composition.facilityId;
  const facilitySchemes = schemes.filter(s => !s.isGlobal);
  const globalSchemes = schemes.filter(s => s.isGlobal);

  const openNew = () => {
    setModalState({ open: true, mode: 'create', source: active });
    setOpen(false);
  };
  const openEdit = () => {
    if (!active) return;
    setModalState({ open: true, mode: 'edit', source: active });
    setOpen(false);
  };
  const openDuplicate = () => {
    if (!active) return;
    setModalState({ open: true, mode: 'duplicate', source: active });
    setOpen(false);
  };
  // Seeds from the widgets rather than from another scheme — see 'from-overlay'
  // in SchemeEditorModal. No source scheme, so none is passed.
  const openFromOverlay = () => {
    setModalState({ open: true, mode: 'from-overlay', source: null });
    setOpen(false);
  };

  return (
    <>
      <div ref={ref} className="relative mt-3 rounded-md border border-gray-200 px-2.5 py-2 dark:border-gray-700">
        <p className="mb-1 flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-gray-400">
          <PaletteIcon className="h-3 w-3" />
          Color Scheme
        </p>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setOpen(v => !v)}
            disabled={isLoading || applyMutation.isPending}
            className="flex flex-1 items-center justify-between gap-2 rounded border border-gray-300 bg-white px-2 py-1.5 text-xs hover:border-gray-400 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:hover:border-gray-500"
          >
            <span className="flex min-w-0 items-center gap-2">
              {active ? <SchemePreview scheme={active} /> : null}
              <span className="truncate text-gray-900 dark:text-gray-100">
                {active ? active.name : 'None'}
              </span>
            </span>
            {applyMutation.isPending ? (
              <Loader2 className="h-3 w-3 shrink-0 animate-spin text-gray-400" />
            ) : (
              <ChevronDown className="h-3 w-3 shrink-0 text-gray-400" />
            )}
          </button>
          {activeIsEditable && (
            <button type="button" onClick={openEdit} title="Edit active scheme"
              className="rounded border border-gray-300 p-1.5 text-gray-500 hover:bg-gray-50 hover:text-gray-700 dark:border-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-200">
              <Pencil className="h-3 w-3" />
            </button>
          )}
        </div>

        {open && (
          <div className="absolute left-2.5 right-2.5 top-full z-30 mt-1 max-h-64 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900">
            {facilitySchemes.length > 0 && (
              <>
                <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                  Facility
                </div>
                {facilitySchemes.map(s => (
                  <SchemeRow
                    key={s.id}
                    scheme={s}
                    active={active?.id === s.id}
                    onClick={() => { applyMutation.mutate(s.id); setOpen(false); }}
                  />
                ))}
              </>
            )}
            {globalSchemes.length > 0 && (
              <>
                <div className="border-t border-gray-100 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:border-gray-800">
                  Global Templates
                </div>
                {globalSchemes.map(s => (
                  <SchemeRow
                    key={s.id}
                    scheme={s}
                    active={active?.id === s.id}
                    onClick={() => { applyMutation.mutate(s.id); setOpen(false); }}
                  />
                ))}
              </>
            )}
            {schemes.length === 0 && (
              <p className="px-3 py-3 text-xs text-gray-400">No schemes yet for this facility.</p>
            )}

            <div className="border-t border-gray-100 dark:border-gray-800">
              <button type="button" onClick={openFromOverlay}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20">
                <Download className="h-3 w-3" />
                New scheme from this overlay&apos;s colours…
              </button>
              <button type="button" onClick={openNew}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800">
                <Plus className="h-3 w-3" />
                {/* "from current" used to read as the widgets; it is the active palette. */}
                New scheme{active ? ` from "${active.name}"…` : '…'}
              </button>
              {active && (
                <button type="button" onClick={openDuplicate}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800">
                  <Copy className="h-3 w-3" />
                  Duplicate "{active.name}"…
                </button>
              )}
              {active && (
                <button
                  type="button"
                  onClick={() => { applyMutation.mutate(null); setOpen(false); }}
                  className="block w-full px-3 py-1.5 text-left text-xs text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Detach scheme (use raw widget colors)
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <SchemeEditorModal
        open={modalState.open}
        mode={modalState.mode}
        source={modalState.source}
        facilityId={composition.facilityId}
        compositionId={composition.id}
        onClose={() => setModalState(s => ({ ...s, open: false }))}
      />
    </>
  );
}

function SchemeRow({ scheme, active, onClick }: { scheme: ColorScheme; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-blue-50 dark:hover:bg-blue-900/20 ${
        active ? 'bg-blue-50 dark:bg-blue-900/20' : ''
      }`}
    >
      <SchemePreview scheme={scheme} />
      <span className="flex-1 truncate text-gray-900 dark:text-gray-100">{scheme.name}</span>
      {scheme.isDefault && <span className="text-[10px] text-gray-400">default</span>}
      {active && <Check className="h-3 w-3 shrink-0 text-blue-500" />}
    </button>
  );
}

// ─── Overlay Card ─────────────────────────────────────────────────────────────

function OverlayCard({
  composition, onEdit, onDelete, onPublish, onUnpublish, onDuplicate, onSaveAsTemplate, onRename, onEventNameChange,
}: {
  composition: OverlayComposition;
  onEdit: () => void;
  onDelete: () => void;
  onPublish: () => void;
  onUnpublish: () => void;
  onDuplicate: () => void;
  onSaveAsTemplate: () => void;
  onRename: (name: string) => void;
  onEventNameChange: (eventName: string | null) => void;
}) {
  const [renaming, setRenaming] = useState(false);
  const [renameVal, setRenameVal] = useState(composition.name);
  const inputRef = useRef<HTMLInputElement>(null);
  const sceneCount = composition.scenes?.length ?? 0;
  const outputUrl = `${window.location.origin}/overlay/${composition.facilityId}/${composition.sheetId}`;

  // Event name is auto-saved on blur. Local state lets the operator type without
  // firing a request on every keystroke; we only persist when focus leaves.
  const [eventName, setEventName] = useState(composition.eventName ?? '');
  useEffect(() => { setEventName(composition.eventName ?? ''); }, [composition.eventName]);
  const commitEventName = () => {
    const trimmed = eventName.trim();
    const current = composition.eventName ?? '';
    if (trimmed === current) return;
    onEventNameChange(trimmed === '' ? null : trimmed);
  };

  const commitRename = () => {
    const trimmed = renameVal.trim();
    if (trimmed && trimmed !== composition.name) onRename(trimmed);
    setRenaming(false);
  };

  useEffect(() => { if (renaming) inputRef.current?.select(); }, [renaming]);

  return (
    <Card className="flex flex-col">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            {renaming ? (
              <input ref={inputRef} value={renameVal} onChange={e => setRenameVal(e.target.value)}
                onBlur={commitRename}
                onKeyDown={e => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') { setRenameVal(composition.name); setRenaming(false); } }}
                className="w-full rounded border border-blue-400 bg-white px-2 py-0.5 text-sm font-semibold text-gray-900 focus:outline-none dark:bg-gray-800 dark:text-gray-100" />
            ) : (
              <div className="group flex items-center gap-1">
                <CardTitle className="text-base leading-snug">{composition.name}</CardTitle>
                <button onClick={() => { setRenameVal(composition.name); setRenaming(true); }}
                  className="invisible rounded p-0.5 text-gray-400 hover:text-gray-600 group-hover:visible dark:hover:text-gray-300"
                  title="Rename">
                  <Pencil className="h-3 w-3" />
                </button>
              </div>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <span className="rounded bg-blue-100 px-1.5 py-0.5 text-xs font-semibold text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
              Sheet {composition.sheetId}
            </span>
            {composition.isPublished && (
              <Badge className="border-0 bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
                Published
              </Badge>
            )}
          </div>
        </div>
        {composition.description && (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{composition.description}</p>
        )}
      </CardHeader>

      <CardContent className="flex-1 pb-3">
        <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
          <Layers className="h-4 w-4" />
          <span>{sceneCount} scene{sceneCount !== 1 ? 's' : ''}</span>
          <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">{composition.facilityId}</span>
        </div>

        <div className="mt-3">
          <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
            Event Name
          </label>
          <input
            type="text"
            value={eventName}
            onChange={e => setEventName(e.target.value)}
            onBlur={commitEventName}
            onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
            placeholder="e.g. Provincials 2026"
            className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 placeholder:text-gray-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
          />
        </div>

        <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 px-2.5 py-2 dark:border-gray-700 dark:bg-gray-800/50">
          <p className="mb-1 text-xs font-medium text-gray-500 dark:text-gray-400">Browser Source URL</p>
          <div className="flex items-center gap-1">
            <span className="flex-1 truncate font-mono text-xs text-gray-600 dark:text-gray-300">{outputUrl}</span>
            <CopyButton text={outputUrl} />
          </div>
          {!composition.isPublished && (
            <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">Publish to activate</p>
          )}
        </div>

        <SchemePicker composition={composition} />
      </CardContent>

      <CardFooter className="flex flex-wrap gap-1.5 border-t border-gray-100 pt-3 dark:border-gray-700">
        <Button size="sm" variant="outline" onClick={onEdit} className="flex-1">
          <Edit className="mr-1 h-3.5 w-3.5" />Edit
        </Button>
        {composition.isPublished ? (
          <Button size="sm" variant="outline" onClick={onUnpublish} className="flex-1">
            <Lock className="mr-1 h-3.5 w-3.5" />Unpublish
          </Button>
        ) : (
          <Button size="sm" variant="outline" onClick={onPublish} className="flex-1">
            <Globe className="mr-1 h-3.5 w-3.5" />Publish
          </Button>
        )}
        <button title="Duplicate" onClick={onDuplicate}
          className="rounded-md border border-gray-300 p-2 text-gray-500 hover:bg-gray-50 hover:text-gray-700 dark:border-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-200">
          <Copy className="h-3.5 w-3.5" />
        </button>
        <button title="Save as Template" onClick={onSaveAsTemplate}
          className="rounded-md border border-gray-300 p-2 text-gray-500 hover:bg-amber-50 hover:text-amber-600 dark:border-gray-600 dark:hover:bg-amber-900/20 dark:hover:text-amber-400">
          <Star className="h-3.5 w-3.5" />
        </button>
        <button title="Delete" onClick={onDelete}
          className="rounded-md border border-gray-300 p-2 text-red-500 hover:bg-red-50 dark:border-gray-600 dark:hover:bg-red-900/20">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </CardFooter>
    </Card>
  );
}

// ─── Template Card ────────────────────────────────────────────────────────────

function TemplateCard({
  composition, onUse, onDelete, onUnmark,
}: {
  composition: OverlayComposition;
  onUse: () => void;
  onDelete: () => void;
  onUnmark: () => void;
}) {
  const sceneCount = composition.scenes?.length ?? 0;
  return (
    <Card className="flex flex-col border-amber-200 dark:border-amber-800/50">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base leading-snug">{composition.name}</CardTitle>
          <Badge className="shrink-0 border-0 bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
            Template
          </Badge>
        </div>
        {composition.description && (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{composition.description}</p>
        )}
      </CardHeader>
      <CardContent className="flex-1 pb-3">
        <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
          <Layers className="h-4 w-4" />
          <span>{sceneCount} scene{sceneCount !== 1 ? 's' : ''}</span>
        </div>
      </CardContent>
      <CardFooter className="flex gap-1.5 border-t border-gray-100 pt-3 dark:border-gray-700">
        <Button size="sm" onClick={onUse} className="flex-1">
          <Plus className="mr-1 h-3.5 w-3.5" />Use Template
        </Button>
        <button onClick={onUnmark}
          className="rounded-md border border-gray-300 p-2 text-gray-500 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800"
          title="Unmark as template">
          <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
        </button>
        <button title="Delete template" onClick={onDelete}
          className="rounded-md border border-gray-300 p-2 text-red-500 hover:bg-red-50 dark:border-gray-600 dark:hover:bg-red-900/20">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </CardFooter>
    </Card>
  );
}

// ─── New Overlay Split Button ─────────────────────────────────────────────────

function NewOverlayButton({ onBlank, onFromTemplate, hasTemplates }: {
  onBlank: () => void;
  onFromTemplate: () => void;
  hasTemplates: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative flex">
      <Button onClick={onBlank} className="rounded-r-none pr-3">
        <Plus className="mr-2 h-4 w-4" />New Overlay
      </Button>
      <Button onClick={() => setOpen(v => !v)} className="rounded-l-none border-l border-blue-700 px-2">
        <ChevronDown className="h-4 w-4" />
      </Button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-1 w-48 rounded-md border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900">
          <button onClick={() => { setOpen(false); onBlank(); }}
            className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800">
            <Plus className="h-4 w-4 text-gray-400" />Blank Overlay
          </button>
          <button onClick={() => { setOpen(false); onFromTemplate(); }}
            disabled={!hasTemplates}
            className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:text-gray-200 dark:hover:bg-gray-800">
            <LayoutTemplate className="h-4 w-4 text-gray-400" />From Template
            {!hasTemplates && <span className="ml-auto text-xs text-gray-400">None yet</span>}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Template Picker Modal ────────────────────────────────────────────────────

function TemplatePickerModal({ open, templates, onPick, onClose }: {
  open: boolean;
  templates: OverlayComposition[];
  onPick: (template: OverlayComposition) => void;
  onClose: () => void;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl dark:bg-gray-900">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Choose a Template</h2>
          <button onClick={onClose} className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="max-h-80 overflow-y-auto p-4">
          {templates.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-400">No templates yet.</p>
          ) : (
            <div className="space-y-2">
              {templates.map(t => (
                <button key={t.id} onClick={() => onPick(t)}
                  className="flex w-full items-center gap-3 rounded-lg border border-gray-200 p-3 text-left hover:border-blue-400 hover:bg-blue-50 dark:border-gray-700 dark:hover:border-blue-600 dark:hover:bg-blue-900/20">
                  <LayoutTemplate className="h-5 w-5 shrink-0 text-amber-500" />
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 dark:text-gray-100">{t.name}</p>
                    <p className="text-xs text-gray-500">{t.scenes?.length ?? 0} scenes{t.description ? ` · ${t.description}` : ''}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Event Overlay Card ───────────────────────────────────────────────────────
//
// Visually distinct from a regular overlay card so operators don't confuse a
// facility-wide override with a per-sheet overlay.

function EventOverlayCard({
  composition, onEdit, onDelete, onToggleActive, onRename, onEventNameChange, anyOtherActiveAtFacility,
}: {
  composition: OverlayComposition;
  onEdit: () => void;
  onDelete: () => void;
  onToggleActive: (active: boolean) => void;
  onRename: (name: string) => void;
  onEventNameChange: (eventName: string | null) => void;
  anyOtherActiveAtFacility: boolean;
}) {
  const [renaming, setRenaming] = useState(false);
  const [renameVal, setRenameVal] = useState(composition.name);
  const inputRef = useRef<HTMLInputElement>(null);
  const sceneCount = composition.scenes?.length ?? 0;
  const active = composition.isOverrideActive;

  const [eventName, setEventName] = useState(composition.eventName ?? '');
  useEffect(() => { setEventName(composition.eventName ?? ''); }, [composition.eventName]);
  const commitEventName = () => {
    const trimmed = eventName.trim();
    if (trimmed === (composition.eventName ?? '')) return;
    onEventNameChange(trimmed === '' ? null : trimmed);
  };

  const commitRename = () => {
    const trimmed = renameVal.trim();
    if (trimmed && trimmed !== composition.name) onRename(trimmed);
    setRenaming(false);
  };
  useEffect(() => { if (renaming) inputRef.current?.select(); }, [renaming]);

  return (
    <Card className={`flex flex-col ${active ? 'border-2 border-amber-400 shadow-lg shadow-amber-100 dark:shadow-amber-900/20' : 'border-amber-200/60 dark:border-amber-800/30'}`}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            {renaming ? (
              <input ref={inputRef} value={renameVal} onChange={e => setRenameVal(e.target.value)}
                onBlur={commitRename}
                onKeyDown={e => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') { setRenameVal(composition.name); setRenaming(false); } }}
                className="w-full rounded border border-amber-400 bg-white px-2 py-0.5 text-sm font-semibold text-gray-900 focus:outline-none dark:bg-gray-800 dark:text-gray-100" />
            ) : (
              <div className="group flex items-center gap-1">
                <Sparkles className="h-4 w-4 shrink-0 text-amber-500" />
                <CardTitle className="text-base leading-snug">{composition.name}</CardTitle>
                <button onClick={() => { setRenameVal(composition.name); setRenaming(true); }}
                  className="invisible rounded p-0.5 text-gray-400 hover:text-gray-600 group-hover:visible dark:hover:text-gray-300"
                  title="Rename">
                  <Pencil className="h-3 w-3" />
                </button>
              </div>
            )}
          </div>
          {active && (
            <Badge className="shrink-0 border-0 bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
              <Radio className="mr-1 h-3 w-3 animate-pulse" />LIVE
            </Badge>
          )}
        </div>
        {composition.description && (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{composition.description}</p>
        )}
      </CardHeader>

      <CardContent className="flex-1 pb-3">
        <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
          <Layers className="h-4 w-4" />
          <span>{sceneCount} scene{sceneCount !== 1 ? 's' : ''}</span>
          <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">{composition.facilityId}</span>
        </div>

        <div className="mt-3">
          <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
            Event Name
          </label>
          <input
            type="text"
            value={eventName}
            onChange={e => setEventName(e.target.value)}
            onBlur={commitEventName}
            onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
            placeholder="e.g. Provincials 2026"
            className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 placeholder:text-gray-400 focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
          />
        </div>

        <SchemePicker composition={composition} />

        <div className={`mt-3 rounded-md border px-3 py-2 ${active ? 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20' : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50'}`}>
          <label className="flex cursor-pointer items-center justify-between gap-3">
            <span className="min-w-0">
              <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                Apply to all sheets
              </span>
              <span className="block text-[11px] text-gray-500 dark:text-gray-400">
                {active
                  ? 'This overlay is live on every sheet at this facility.'
                  : anyOtherActiveAtFacility
                    ? 'Enabling will switch the facility from the currently-active event overlay.'
                    : 'Replaces per-sheet overlays until turned off.'}
              </span>
            </span>
            <input
              type="checkbox"
              checked={active}
              onChange={e => onToggleActive(e.target.checked)}
              className="h-5 w-9 shrink-0 cursor-pointer appearance-none rounded-full bg-gray-300 transition-colors checked:bg-amber-500 dark:bg-gray-600 relative
                before:absolute before:left-0.5 before:top-0.5 before:h-4 before:w-4 before:rounded-full before:bg-white before:transition-transform
                checked:before:translate-x-4"
            />
          </label>
        </div>
      </CardContent>

      <CardFooter className="flex gap-1.5 border-t border-gray-100 pt-3 dark:border-gray-700">
        <Button size="sm" variant="outline" onClick={onEdit} className="flex-1">
          <Edit className="mr-1 h-3.5 w-3.5" />Edit
        </Button>
        <button title="Delete" onClick={onDelete}
          className="rounded-md border border-gray-300 p-2 text-red-500 hover:bg-red-50 dark:border-gray-600 dark:hover:bg-red-900/20">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </CardFooter>
    </Card>
  );
}

// ─── Event Source Picker Modal ────────────────────────────────────────────────
// Choose an existing same-facility per-sheet overlay to clone into a new
// event override.

function EventSourcePickerModal({
  open, sources, onPick, onClose,
}: {
  open: boolean;
  sources: OverlayComposition[];
  onPick: (source: OverlayComposition, name: string) => void;
  onClose: () => void;
}) {
  const [selected, setSelected] = useState<OverlayComposition | null>(null);
  const [name, setName] = useState('');

  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setName('');
  }, [open]);

  useEffect(() => {
    if (selected) setName(`Event: ${selected.name}`);
  }, [selected]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-lg bg-white shadow-xl dark:bg-gray-900">
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">New Event Overlay</h2>
          <button onClick={onClose} className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
          <div>
            <p className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              Clone from existing overlay
            </p>
            {sources.length === 0 ? (
              <p className="rounded-md border border-dashed border-gray-300 py-6 text-center text-sm text-gray-400 dark:border-gray-700">
                No source overlays available at this facility.
              </p>
            ) : (
              <div className="max-h-60 space-y-1 overflow-y-auto">
                {sources.map(s => (
                  <button
                    key={s.id}
                    onClick={() => setSelected(s)}
                    className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left ${
                      selected?.id === s.id
                        ? 'border-amber-400 bg-amber-50 dark:border-amber-500 dark:bg-amber-900/20'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:hover:border-gray-600 dark:hover:bg-gray-800'
                    }`}
                  >
                    <Layers className="h-5 w-5 shrink-0 text-gray-400" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{s.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Sheet {s.sheetId} · {s.scenes?.length ?? 0} scene{(s.scenes?.length ?? 0) !== 1 ? 's' : ''}
                      </p>
                    </div>
                    {selected?.id === s.id && <Check className="h-4 w-4 shrink-0 text-amber-500" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          {selected && (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                autoFocus
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. Provincials 2026"
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              />
            </div>
          )}
        </div>

        <div className="flex shrink-0 justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => selected && name.trim() && onPick(selected, name.trim())}
            disabled={!selected || !name.trim()}
          >
            Create
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function OverlaysPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedFacility, setSelectedFacility } = useAdminFacility();

  const [showNewModal, setShowNewModal] = useState(false);
  const [newMode, setNewMode] = useState<'blank' | 'template' | 'duplicate'>('blank');
  const [selectedTemplate, setSelectedTemplate] = useState<OverlayComposition | null>(null);
  const [duplicateSource, setDuplicateSource] = useState<OverlayComposition | null>(null);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [showEventSourcePicker, setShowEventSourcePicker] = useState(false);

  const queryParams: { facilityId?: string } = {};
  if (isAdmin && selectedFacility) queryParams.facilityId = selectedFacility;
  else if (!isAdmin && user?.facilityId) queryParams.facilityId = user.facilityId;

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.overlays.compositions(queryParams),
    queryFn: () => overlaysApi.listCompositions(queryParams),
  });

  const { data: allData } = useQuery({
    queryKey: queryKeys.overlays.compositions({}),
    queryFn: () => overlaysApi.listCompositions({}),
    enabled: isAdmin,
  });

  const { data: facilitiesData } = useQuery({
    queryKey: queryKeys.facilities.list(),
    queryFn: () => facilitiesApi.list({ limit: 100 }),
    enabled: isAdmin,
  });

  const publishMutation = useMutation({
    mutationFn: (id: number) => overlaysApi.publishComposition(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.overlays.all }),
  });
  const unpublishMutation = useMutation({
    mutationFn: (id: number) => overlaysApi.unpublishComposition(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.overlays.all }),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: number) => overlaysApi.deleteComposition(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.overlays.all }),
  });
  const openDuplicate = (comp: OverlayComposition) => {
    setDuplicateSource(comp);
    setNewMode('duplicate');
    setSelectedTemplate(null);
    setShowNewModal(true);
  };
  const renameMutation = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) => overlaysApi.updateComposition(id, { name }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.overlays.all }),
  });
  const eventNameMutation = useMutation({
    mutationFn: ({ id, eventName }: { id: number; eventName: string | null }) =>
      overlaysApi.updateComposition(id, { eventName }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.overlays.all }),
  });
  const saveAsTemplateMutation = useMutation({
    mutationFn: (id: number) => overlaysApi.updateComposition(id, { isTemplate: true, isPublished: false }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.overlays.all }),
  });
  const unmarkTemplateMutation = useMutation({
    mutationFn: (id: number) => overlaysApi.updateComposition(id, { isTemplate: false }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.overlays.all }),
  });
  const cloneAsEventOverrideMutation = useMutation({
    mutationFn: ({ sourceId, name }: { sourceId: number; name: string }) =>
      overlaysApi.cloneAsEventOverride(sourceId, { name }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.overlays.all }),
  });
  const toggleOverrideMutation = useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) =>
      overlaysApi.updateComposition(id, { isOverrideActive: active }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.overlays.all }),
  });

  const allCompositions: OverlayComposition[] = data?.compositions || [];
  const allCompositionsGlobal: OverlayComposition[] = isAdmin ? (allData?.compositions || allCompositions) : allCompositions;
  const overlays = allCompositions
    .filter(c => !c.isTemplate && !c.isEventOverride)
    .sort((a, b) => a.name.localeCompare(b.name));
  const eventOverrides = allCompositions
    .filter(c => c.isEventOverride)
    .sort((a, b) => a.name.localeCompare(b.name));
  const activeOverride = eventOverrides.find(c => c.isOverrideActive) ?? null;
  const templates = allCompositions.filter(c => c.isTemplate).sort((a, b) => a.name.localeCompare(b.name));
  const facilities = facilitiesData?.facilities || [];
  const defaultFacilityId = isAdmin
    ? (selectedFacility || facilities[0]?.facilityId || '')
    : (user?.facilityId || '');

  const openFromTemplate = () => {
    if (templates.length === 1) {
      setSelectedTemplate(templates[0]!);
      setNewMode('template');
      setShowNewModal(true);
    } else {
      setShowTemplatePicker(true);
    }
  };

  return (
    <div className="space-y-6 p-6">
      {/* Active event override banner */}
      {activeOverride && (
        <div className="flex items-start gap-3 rounded-lg border-2 border-amber-300 bg-amber-50 px-4 py-3 dark:border-amber-700 dark:bg-amber-900/20">
          <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
              Event overlay <span className="font-bold">"{activeOverride.name}"</span> is live on every sheet at {activeOverride.facilityId}.
            </p>
            <p className="mt-0.5 text-xs text-amber-700 dark:text-amber-300">
              Turn off the "Apply to all sheets" toggle on its card to restore per-sheet overlays.
            </p>
          </div>
          <button
            onClick={() => toggleOverrideMutation.mutate({ id: activeOverride.id, active: false })}
            disabled={toggleOverrideMutation.isPending}
            className="shrink-0 rounded-md border border-amber-400 bg-white px-3 py-1 text-xs font-medium text-amber-700 hover:bg-amber-100 disabled:opacity-50 dark:border-amber-700 dark:bg-amber-900/40 dark:text-amber-200 dark:hover:bg-amber-900/60"
          >
            Turn off
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Overlays</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Manage overlay layouts for each sheet.</p>
        </div>
        <NewOverlayButton
          hasTemplates={templates.length > 0}
          onBlank={() => { setNewMode('blank'); setSelectedTemplate(null); setShowNewModal(true); }}
          onFromTemplate={openFromTemplate}
        />
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : error ? (
        <div className="flex items-center gap-2 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">
          <AlertCircle className="h-4 w-4" />Failed to load overlays
        </div>
      ) : (
        <>
          {/* Overlays grid */}
          {overlays.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 py-16 text-center dark:border-gray-700">
              <Layers className="mb-3 h-10 w-10 text-gray-300 dark:text-gray-600" />
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">No overlays yet</p>
              <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">Create one to get started.</p>
              <Button className="mt-4" size="sm" onClick={() => { setNewMode('blank'); setSelectedTemplate(null); setShowNewModal(true); }}>
                <Plus className="mr-1 h-4 w-4" />New Overlay
              </Button>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {overlays.map(comp => (
                <OverlayCard
                  key={comp.id}
                  composition={comp}
                  onEdit={() => navigate(`/overlays/${comp.id}`)}
                  onDelete={() => { if (confirm(`Delete "${comp.name}"? This cannot be undone.`)) deleteMutation.mutate(comp.id); }}
                  onPublish={() => publishMutation.mutate(comp.id)}
                  onUnpublish={() => unpublishMutation.mutate(comp.id)}
                  onDuplicate={() => openDuplicate(comp)}
                  onSaveAsTemplate={() => { if (confirm(`Save "${comp.name}" as a template? It will be unpublished and moved to the Templates section.`)) saveAsTemplateMutation.mutate(comp.id); }}
                  onRename={name => renameMutation.mutate({ id: comp.id, name })}
                  onEventNameChange={eventName => eventNameMutation.mutate({ id: comp.id, eventName })}
                />
              ))}
            </div>
          )}

          {/* Event Overlays section */}
          <div className="pt-2">
            <div className="mb-3 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-amber-500" />
                <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Event Overlays</h2>
                <span className="text-xs text-gray-400">
                  {eventOverrides.length === 0
                    ? 'Optional facility-wide overrides for events'
                    : `${eventOverrides.length} event overlay${eventOverrides.length !== 1 ? 's' : ''}`}
                </span>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowEventSourcePicker(true)}
                disabled={overlays.length === 0}
                title={overlays.length === 0 ? 'Create at least one regular overlay first' : undefined}
              >
                <Plus className="mr-1 h-4 w-4" />New Event Overlay
              </Button>
            </div>
            {eventOverrides.length > 0 && (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {eventOverrides.map(comp => (
                  <EventOverlayCard
                    key={comp.id}
                    composition={comp}
                    onEdit={() => navigate(`/overlays/${comp.id}`)}
                    onDelete={() => {
                      if (comp.isOverrideActive) {
                        if (!confirm(`"${comp.name}" is currently live on all sheets. Delete anyway?`)) return;
                      } else if (!confirm(`Delete event overlay "${comp.name}"? This cannot be undone.`)) return;
                      deleteMutation.mutate(comp.id);
                    }}
                    onToggleActive={active => toggleOverrideMutation.mutate({ id: comp.id, active })}
                    onRename={name => renameMutation.mutate({ id: comp.id, name })}
                    onEventNameChange={eventName => eventNameMutation.mutate({ id: comp.id, eventName })}
                    anyOtherActiveAtFacility={
                      !!activeOverride && activeOverride.id !== comp.id && activeOverride.facilityId === comp.facilityId
                    }
                  />
                ))}
              </div>
            )}
          </div>

          {/* Templates section */}
          {templates.length > 0 && (
            <div className="pt-2">
              <div className="mb-3 flex items-center gap-2">
                <LayoutTemplate className="h-4 w-4 text-amber-500" />
                <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Templates</h2>
                <span className="text-xs text-gray-400">{templates.length} template{templates.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {templates.map(t => (
                  <TemplateCard
                    key={t.id}
                    composition={t}
                    onUse={() => { setSelectedTemplate(t); setNewMode('template'); setShowNewModal(true); }}
                    onDelete={() => { if (confirm(`Delete template "${t.name}"? This cannot be undone.`)) deleteMutation.mutate(t.id); }}
                    onUnmark={() => unmarkTemplateMutation.mutate(t.id)}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      <TemplatePickerModal
        open={showTemplatePicker}
        templates={templates}
        onPick={t => { setSelectedTemplate(t); setNewMode('template'); setShowTemplatePicker(false); setShowNewModal(true); }}
        onClose={() => setShowTemplatePicker(false)}
      />
      <EventSourcePickerModal
        open={showEventSourcePicker}
        sources={overlays}
        onPick={(source, name) => {
          cloneAsEventOverrideMutation.mutate({ sourceId: source.id, name });
          setShowEventSourcePicker(false);
        }}
        onClose={() => setShowEventSourcePicker(false)}
      />
      <NewOverlayModal
        open={showNewModal}
        mode={newMode}
        template={selectedTemplate}
        duplicateSource={duplicateSource}
        allCompositions={allCompositionsGlobal}
        onClose={() => setShowNewModal(false)}
        onCreated={(facilityId) => { if (isAdmin && facilityId !== selectedFacility) setSelectedFacility(facilityId); }}
        defaultFacilityId={defaultFacilityId}
        facilities={facilities}
        isAdmin={isAdmin}
      />
    </div>
  );
}
